import { useState, useEffect, useCallback } from 'react';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { collection, query, getDocs, addDoc, updateDoc, doc, orderBy, getDoc, deleteDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { Exhibition } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { getMuseumLanguages } from '@/shared/utils/languageUtils';

export interface CreateExhibitionData {
  name: Record<string, string>;
  description: Record<string, string>;
  cover: string;
  status: Record<string, 'draft' | 'published'>; // Statut par langue
  color?: string;
  order?: number;
  durationTimeInMinutes?: number;
  episodesNumber?: number;
  isTemporary?: boolean;
}

export const useExhibitions = () => {
  const { museum } = useMuseum();
  const [exhibitions, setExhibitions] = useState<Exhibition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExhibitions = useCallback(async () => {
    if (!museum?.id) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    if (!db) {
      setError('Firebase DB not initialized');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      logger.log('museum', museum);
      const exhibitionsRef = collection(
        db,
        FIRESTORE_COLLECTIONS.MUSEUMS,
        museum.id,
        FIRESTORE_COLLECTIONS.EXHIBITIONS
      );
      const q = query(exhibitionsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const exhibitionsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Exhibition[];
      setExhibitions(exhibitionsData);
    } catch (err) {
      logger.error('Erreur lors de la récupération des expositions:', err);
      setError('Erreur lors de la récupération des expositions');
    } finally {
      setLoading(false);
    }
  }, [museum]);

  const createExhibition = useCallback(
    async (data: CreateExhibitionData): Promise<string | null> => {
      if (!museum?.id) {
        setError('Musée non trouvé');
        return null;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return null;
      }

      try {
        setError(null);
        const exhibitionsRef = collection(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS
        );
        const exhibitionData = {
          ...data,
          durationTimeInMinutes: data.durationTimeInMinutes || 0,
          episodesNumber: data.episodesNumber || 0,
          isTemporary: data.isTemporary || false,
          createdAt: Object.fromEntries(getMuseumLanguages(museum).map(lang => [lang, new Date()])),
          updatedAt: Object.fromEntries(getMuseumLanguages(museum).map(lang => [lang, new Date()])),
        };
        const docRef = await addDoc(exhibitionsRef, exhibitionData);
        await fetchExhibitions(); // Recharger la liste
        return docRef.id;
      } catch (err) {
        logger.error('Erreur lors de la création de l\'exposition:', err);
        setError('Erreur lors de la création de l\'exposition');
        return null;
      }
    },
    [museum, fetchExhibitions]
  );

  const updateExhibition = useCallback(
    async (id: string, data: Partial<Exhibition>): Promise<boolean> => {
      if (!museum?.id) {
        setError('Musée non trouvé');
        return false;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return false;
      }

      try {
        setError(null);
        const exhibitionRef = doc(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS,
          id
        );
        // Préparer updatedAt par langue (conserver createdAt existant)
        // Récupérer les langues depuis data.name ou depuis l'exposition existante ou du musée
        const exhibitionDoc = await getDoc(exhibitionRef);
        const existingData = exhibitionDoc.data() as Exhibition | undefined;
        const languages = data.name 
          ? Object.keys(data.name) 
          : (existingData?.name ? Object.keys(existingData.name) : getMuseumLanguages(museum));
        
        const now = new Date();
        const updatedAtByLang: Record<string, Date> = {};
        languages.forEach((lang) => {
          updatedAtByLang[lang] = now;
        });

        await updateDoc(exhibitionRef, {
          ...data,
          updatedAt: updatedAtByLang,
        });
        await fetchExhibitions(); // Recharger la liste
        return true;
      } catch (err) {
        logger.error('Erreur lors de la mise à jour de l\'exposition:', err);
        setError('Erreur lors de la mise à jour de l\'exposition');
        return false;
      }
    },
    [museum, fetchExhibitions]
  );

  const deleteExhibition = useCallback(
    async (id: string): Promise<boolean> => {
      if (!museum?.id) {
        setError('Musée non trouvé');
        return false;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return false;
      }

      try {
        setError(null);
        const exhibitionRef = doc(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS,
          id
        );
        await deleteDoc(exhibitionRef);
        await fetchExhibitions(); // Recharger la liste
        return true;
      } catch (err) {
        logger.error('Erreur lors de la suppression de l\'exposition:', err);
        setError('Erreur lors de la suppression de l\'exposition');
        return false;
      }
    },
    [museum, fetchExhibitions]
  );

  useEffect(() => {
    fetchExhibitions();
  }, [fetchExhibitions]);

  return {
    exhibitions,
    loading,
    error,
    createExhibition,
    updateExhibition,
    deleteExhibition,
    refetch: fetchExhibitions,
  };
};

